import { CommonModule } from '@angular/common';
import {
  Component,
  forwardRef,
  Injector,
  Input,
  OnInit,
  input
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';

import { FoxeraInputTextComponent } from '@foxera/shared/components/foxera-input-text/foxera-input-text.component';
import { FoxeraInputConfig } from '@foxera/shared/interfaces/foxera-input-text.interface';

@Component({
  selector: 'foxera-email-input',
  templateUrl: '../foxera-input-text/foxera-input-text.component.html',
  styleUrls: ['../foxera-input-text/foxera-input-text.component.css'],
  imports: [CommonModule, FormsModule, InputText, Message],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FoxeraEmailInputComponent),
      multi: true,
    },
  ],
})
export class FoxeraEmailInputComponent
  extends FoxeraInputTextComponent
  implements OnInit
{
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() existingEmails: string[] = []; // List of existing emails to check against
  readonly disabled = input<boolean>(false); // Override for edit mode
  readonly label = input<string>('Email');
  readonly placeholder = input<string>('Enter your email');

  constructor(injector: Injector) {
    super(injector);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Set up email-specific configuration
    this.updateEmailConfig();
    this.config = this.emailConfig;
  }

  // Performance optimization: property instead of getter
  private emailConfig: FoxeraInputConfig = {
    label: '',
    placeholder: '',
    type: 'email',
    required: true,
    disabled: false,
    isUnique: true,
    uniqueList: [],
    showLoadingIndicator: true,
    uniqueErrorMessage: 'This email is already in use.',
    requiredErrorMessage: 'Email address is required.',
    autocomplete: 'off',
  };

  private updateEmailConfig(): void {
    this.emailConfig = {
      label: this.label(),
      placeholder: this.placeholder(),
      type: 'email',
      required: true,
      disabled: this.disabled(),
      isUnique: true,
      uniqueList: this.existingEmails,
      showLoadingIndicator: true,
      uniqueErrorMessage: 'This email is already in use.',
      requiredErrorMessage: 'Email address is required.',
      autocomplete: 'off',
    };
  }

  // Override input handler to update config when existingEmails change
  override onInput(event: Event): void {
    super.onInput(event); // Call parent implementation

    // Update config to ensure uniqueList is current
    this.config = this.emailConfig;
  }

  // Email format validation helper (can be used externally if needed)
  public isValidEmailFormat(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Email validation check
  public isEmailValid(): boolean {
    // For disabled mode (edit), email is always valid
    if (this.disabled()) {
      return true;
    }

    // For enabled mode, check basic validation and uniqueness
    const email = this.value;
    if (!email || !this.isValidEmailFormat(email)) {
      return false;
    }

    // Check uniqueness using the parent component's unique validation
    return !this.hasError;
  }

  // Update config when existingEmails changes
  updateExistingEmails(emails: string[]): void {
    this.existingEmails = emails;
    this.config = this.emailConfig;
  }
}
